'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { Faders } from 'phosphor-icons';
import { useState } from 'react';
import { c } from 'ui-components';
import { FiltersDrawer } from '../FiltersDrawer';
import { PriceFacet } from '../PriceFacet';
import { PublisherFacet } from '../PublisherFacet';
import { RatingFacet } from '../RatingFacet';
import { ReleaseDateFacet } from '../ReleaseDateFacet';
import { ResultsPerPage } from '../ResultsPerPage';
import { HorizontalFiltersSkeleton } from '../Skeleton/HorizontalFiltersSkeleton';
import { Sort } from '../Sort';
import { useSearchLoading } from '../hooks/useSearchLoading';
import { HorizontalFiltersProps } from './HorizontalFilters.types';

export const HorizontalFilters = ({ className }: HorizontalFiltersProps) => {
  const { isInitialLoad } = useSearchLoading();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
  }>();

  // Both the group aria-label and the drawer-trigger button reuse the flat
  // `search.filters` key (the only filters label that exists in the CMS set).
  const filtersLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.filters')?.value ??
    'Filters';

  if (isInitialLoad) {
    return <HorizontalFiltersSkeleton className={className} />;
  }

  return (
    <div
      role="group"
      aria-label={filtersLabel}
      className={c('items-end gap-3', className)}
    >
      {/* Filter pills */}
      <div className="flex items-center gap-2">
        <PriceFacet />
        <RatingFacet />
        <PublisherFacet className="hidden xl:block" />
        <ReleaseDateFacet className="hidden xl:block" />
        <button
          type="button"
          onClick={() => setIsDrawerOpen(true)}
          aria-haspopup="dialog"
          className={c(
            'flex items-center gap-x-2 rounded-lg border p-2 text-sm font-normal xl:hidden',
            'focus-outline',
            'border-grey-400 bg-transparent text-[#141414]'
          )}
        >
          <Faders size={14} />
          {filtersLabel}
        </button>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Sort by + Results per page (keep their own labels) */}
      <Sort />
      <ResultsPerPage />

      <FiltersDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </div>
  );
};
